import { Component, OnInit, Input } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { VerifyAccountResponse } from 'src/app/_models/verify-account';
import { PreferenceCenterURLParams } from '../_models/urlParams';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { showPreferenceCenterInlineLoader, preferenceCenterEmailFound, hidePreferenceCenterInlineLoader, preferenceCenterNoEmailFound } from '../_actions/preference-center.actions';
import { Observable } from 'rxjs';
import { showInlineLoader, hideInlineLoader } from '../_actions/inline-loader.actions';
import { AppConfigService } from '../appconfig/appconfig.service';
import brandInfo from "src/environments/brand.json";

@Component({
  selector: 'app-preference',
  standalone: false,
  templateUrl: './preference.component.html',
  styleUrls: ['./preference.component.scss']
})

@Input()

export class PreferenceComponent implements OnInit {
  urlParameters: PreferenceCenterURLParams = {
    emailId: null,
    data: null,
    subId: null,
    JobID: null,
    BatchID: null
  }
  preferenceCenterInlineLoader$: Observable<any>;
  preferenceCenter$: Observable<any>;
  urlSafe: SafeResourceUrl
  branding: any;

  constructor(
    public sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private appConfig: AppConfigService,
    private router: Router,
    private store: Store<{
      inlineLoaderPreferenceCenter: any,
      preferenceCenter: any
    }>
  ) {
    this.route.queryParams.subscribe(params => {
      this.urlParameters.emailId = params["email"];
      this.urlParameters.data = params["data"];
      this.urlParameters.subId = params["SubKey"];
      this.urlParameters.JobID = params["JobID"];
      this.urlParameters.BatchID = params["BatchID"];
    })
  }

  // Allow-list pattern for opaque tokens/IDs coming from query string.
  // Limited to characters that are valid in URL-safe base64 / common ID formats.
  private static readonly SAFE_TOKEN_REGEX = /^[A-Za-z0-9._\-+/=%]{1,512}$/;
  // Allow-list pattern for the email/subscriber identifier.
  private static readonly SAFE_EMAIL_REGEX = /^[A-Za-z0-9._%+\-@]{1,254}$/;
  private static readonly HUBSPOT_PREFERENCES_HOST = 'hs-47077450.s.hubspotemail.net';

  ngOnInit() {
    this.appConfig.getSettings().subscribe((result) => {
      this.branding = result;
    }, (error) => {});

    this.preferenceCenterInlineLoader$ = this.store.select(store => store.inlineLoaderPreferenceCenter);
    this.preferenceCenter$ = this.store.select(store => store.preferenceCenter);

    this.store.dispatch(showInlineLoader());

    if (this.urlParameters.data) {
      if (
        brandInfo.hubspotPreferencesId &&
        PreferenceComponent.SAFE_TOKEN_REGEX.test(brandInfo.hubspotPreferencesId) &&
        PreferenceComponent.SAFE_TOKEN_REGEX.test(this.urlParameters.data)
      ) {
        const hubspotBase = `https://${PreferenceComponent.HUBSPOT_PREFERENCES_HOST}/hs/preferences-center/${encodeURIComponent(brandInfo.hubspotPreferencesId)}/en/page`;
        const candidateUrl = `${hubspotBase}?data=${encodeURIComponent(this.urlParameters.data)}`;
        const safeIframeSrc = this.toSafeSameHostUrl(candidateUrl, PreferenceComponent.HUBSPOT_PREFERENCES_HOST);

        if (safeIframeSrc) {
          const e = document.createElement("iframe");
          e.src = safeIframeSrc;
          e.width = "100%";
          e.height = "500";
          e.style.border = "none";
          const host = document.getElementById("hs-custom-preferences");
          if (host) {
            host.appendChild(e);
          }
          this.store.dispatch(preferenceCenterEmailFound());
        } else {
          this.store.dispatch(preferenceCenterNoEmailFound());
        }
      } else {
        this.store.dispatch(preferenceCenterNoEmailFound());
      }
    } else {
      let paramsPresent = this.urlParameters.emailId && this.urlParameters.subId && this.urlParameters.JobID && this.urlParameters.BatchID;
      let paramsPresentOld = this.urlParameters.emailId ? true : false;

      const baseSalesforce = `${brandInfo.preferenceCenterUrl}`;
      const baseHost = this.getTrustedHost(baseSalesforce);

      if (paramsPresent && baseHost && this.areSubscriptionParamsSafe()) {
        const url =
          `${baseSalesforce}?email=${encodeURIComponent(this.urlParameters.emailId)}` +
          `&SubKey=${encodeURIComponent(this.urlParameters.subId)}` +
          `&JobId=${encodeURIComponent(this.urlParameters.JobID)}` +
          `&BatchId=${encodeURIComponent(this.urlParameters.BatchID)}`;
        const safeUrl = this.toSafeSameHostUrl(url, baseHost);

        if (safeUrl) {
          this.urlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(safeUrl);
          this.store.dispatch(preferenceCenterEmailFound());
        } else {
          this.store.dispatch(preferenceCenterNoEmailFound());
        }
      } else if (
        !paramsPresent &&
        paramsPresentOld &&
        baseHost &&
        PreferenceComponent.SAFE_EMAIL_REGEX.test(this.urlParameters.emailId)
      ) {
        const url = `${baseSalesforce}?subscriber=${encodeURIComponent(this.urlParameters.emailId)}`;
        const safeUrl = this.toSafeSameHostUrl(url, baseHost);

        if (safeUrl) {
          this.urlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(safeUrl);
          this.store.dispatch(preferenceCenterEmailFound());
        } else {
          this.store.dispatch(preferenceCenterNoEmailFound());
        }
      } else {
        this.store.dispatch(preferenceCenterNoEmailFound());
      }
    }
    this.store.dispatch(hideInlineLoader());
  }

  retry() {
    this.ngOnInit();
  }

  private areSubscriptionParamsSafe(): boolean {
    return (
      PreferenceComponent.SAFE_EMAIL_REGEX.test(this.urlParameters.emailId) &&
      PreferenceComponent.SAFE_TOKEN_REGEX.test(this.urlParameters.subId) &&
      PreferenceComponent.SAFE_TOKEN_REGEX.test(this.urlParameters.JobID) &&
      PreferenceComponent.SAFE_TOKEN_REGEX.test(this.urlParameters.BatchID)
    );
  }

  /**
   * Returns the host of a trusted, build-time configured URL, or null if the
   * URL is malformed or not https. Used to lock the iframe src to the same
   * origin before bypassing Angular's resource-URL sanitizer.
   */
  private getTrustedHost(rawUrl: string): string | null {
    try {
      const parsed = new URL(rawUrl);
      return parsed.protocol === 'https:' ? parsed.host : null;
    } catch {
      return null;
    }
  }

  /**
   * Validates the dynamically built URL parses as https and resolves to the
   * expected host before it can be passed to bypassSecurityTrustResourceUrl
   * or assigned to an iframe.src. This neutralizes the DOM XSS sink path
   * flagged by SAST (e.g. javascript: or cross-origin redirects sneaking in
   * through user-controlled query params).
   */
  private toSafeSameHostUrl(rawUrl: string, expectedHost: string): string | null {
    try {
      const parsed = new URL(rawUrl);
      if (parsed.protocol !== 'https:') {
        return null;
      }
      if (parsed.host !== expectedHost) {
        return null;
      }
      return parsed.toString();
    } catch {
      return null;
    }
  }
}